import { useState, useContext } from 'react';
import { toast } from '../lib/toast.js';
import GlobalContext from "../context/GlobalContext.js";
import RequestPasswordResetAPI from '../functions/API/RequestPasswordResetAPI';
import { darkenColor } from '../functions/colorUtils.js';
import { KeyRound, X, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function RequestResetPassword() {
    const { settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const primaryDark = darkenColor(primaryColor);

    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!email.trim()) { toast.error('Please enter your email address'); return; }
        setLoading(true);
        const res = await RequestPasswordResetAPI(email);
        if (res.response?.ok) {
            setSent(true);
        } else {
            toast.error('Something went wrong, please try again');
        }
        setLoading(false);
    }

    const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow';

    function GradientContent({ className }) {
        return (
            <div className={className}>
                <a
                    href="/login"
                    className="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium hover:text-white transition-colors mb-5"
                >
                    <X size={16} strokeWidth={2.5} />
                    Exit
                </a>

                {/* Progress bar — 1 step for request */}
                <div className="flex gap-1.5 mb-8">
                    <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: '#fff' }} />
                    <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: sent ? '#fff' : 'rgba(255,255,255,0.25)' }} />
                </div>

                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <KeyRound size={22} className="text-white" strokeWidth={1.8} />
                    </div>
                    <h1 className="text-2xl font-bold text-white">Reset password</h1>
                </div>
            </div>
        );
    }

    const formContent = sent ? (
        <>
            <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 size={32} className="text-green-600" strokeWidth={1.5} />
                </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-1.5">Check your email</h2>
            <p className="text-sm text-gray-500 text-center mb-8">
                If an account exists for <strong>{email}</strong>, you will receive a password reset email shortly.
            </p>
            <a
                href="/login"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
            >
                Back to Sign In
            </a>
        </>
    ) : (
        <>
            <h2 className="text-xl font-bold text-gray-900 mb-1.5">What's your email address?</h2>
            <p className="text-sm text-gray-500 mb-6">
                Enter the email address associated with your account and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit}>
                <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Email address"
                    className={inputClass}
                    autoFocus
                />

                <button
                    type="submit"
                    disabled={loading}
                    className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                >
                    {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <>Send Reset Link <ArrowRight size={16} /></>
                    )}
                </button>
            </form>

            <p className="mt-8 text-center text-sm text-gray-500">
                Remember your password?{' '}
                <a href="/login" className="font-semibold hover:opacity-80" style={{ color: primaryColor }}>
                    Sign in
                </a>
            </p>
        </>
    );

    return (
        <>
            {/* Mobile */}
            <div className="lg:hidden min-h-screen flex flex-col" style={{ backgroundColor: '#F2F2F7' }}>
                <div
                    className="w-full flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}
                >
                    <GradientContent className="px-5 pt-5 pb-12" />
                </div>
                <div className="flex-1 -mt-6 relative z-10">
                    <div className="bg-[#F2F2F7] rounded-t-[1.5rem] min-h-full">
                        <div className="px-5 pt-7 pb-12">
                            {formContent}
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop */}
            <div className="hidden lg:flex min-h-screen" style={{ backgroundColor: '#F2F2F7' }}>
                <div
                    className="w-1/2 flex items-center justify-center p-12"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}
                >
                    <GradientContent className="w-full max-w-md" />
                </div>
                <div className="w-1/2 flex items-center justify-center p-12">
                    <div className="w-full max-w-md">
                        {formContent}
                    </div>
                </div>
            </div>
        </>
    );
}
